import { useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { invoiceConfirmApi } from '../features/invoiceConfirm/api.ts';
import { Icon } from '../components/Icon.tsx';
import { formatDate } from '../lib/format.ts';
import { t } from '../lib/strings.ts';

// ── Component ────────────────────────────────────────────────────────────
// Public no-login page (vendor's invoiceToken is the capability): shows
// whether a bill has been paid, and — once paid — a prominent link to the
// stored transfer slip. Mirrors InvoiceConfirm.tsx's styling/shell.

export function InvoiceSlip(): JSX.Element {
  const { token } = useParams({ from: '/public-layout/invoice-slip/$token' });

  const { data, isLoading, error } = useQuery({
    queryKey: ['invoice-slip', token],
    queryFn: () => invoiceConfirmApi.getSlipInfo(token),
    retry: false,
  });

  if (isLoading) {
    return (
      <Shell>
        <p className="text-center text-muted-foreground py-16 animate-pulse">
          {t.invoiceSlip.loadingForm}
        </p>
      </Shell>
    );
  }

  if (error || !data) {
    return (
      <Shell>
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <Icon name="cancel" size={40} className="text-destructive" />
          <p className="text-sm text-destructive font-medium">{t.invoiceSlip.invalidLinkTitle}</p>
        </div>
      </Shell>
    );
  }

  if (!data.paid) {
    return (
      <Shell>
        <div className="flex flex-col items-center gap-4 py-16 text-center">
          <Icon name="schedule" size={48} className="text-muted-foreground" />
          <h2 className="text-xl font-bold text-ink">{t.invoiceSlip.notPaidTitle}</h2>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="flex flex-col items-center gap-4 py-16 text-center">
        <Icon name="check_circle" size={48} className="text-[hsl(var(--success))]" />
        <h2 className="text-xl font-bold text-ink">{t.invoiceSlip.paidTitle(data.refNumber)}</h2>
        {data.paidAt && (
          <p className="text-sm text-muted-foreground">
            {t.invoiceSlip.paidAtLabel} <span className="num">{formatDate(data.paidAt)}</span>
          </p>
        )}
        {data.hasSlip && (
          <a
            href={invoiceConfirmApi.slipUrl(token)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary gap-2 mt-2"
          >
            <Icon name="description" size={18} />
            {t.invoiceSlip.viewSlip}
          </a>
        )}
      </div>
    </Shell>
  );
}

// ── Standalone shell (no sidebar/header) ─────────────────────────────────

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen">
      <header className="dock sticky top-0 z-10 px-4 py-3 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-ink truncate">WapPro</p>
          <p className="text-xs text-muted-foreground">{t.invoiceSlip.pageTitle}</p>
        </div>
      </header>
      <main className="mx-auto w-full max-w-2xl px-4 py-6">{children}</main>
    </div>
  );
}
